import { type FC, type KeyboardEvent, type ReactNode, useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  alpha,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from '@mui/material'

interface DeleteAllConfirmationDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onConfirm: () => void | Promise<void>
  readonly title: string
  readonly children: ReactNode
  readonly confirmText?: string
  readonly cancelText?: string
  readonly isDestructive?: boolean
}

interface DialogHeaderProps {
  readonly title: string
}

interface DialogBodyProps {
  readonly children: ReactNode
}

interface DialogFooterProps {
  readonly onClose: () => void
  readonly onConfirm: () => void
  readonly confirmText: string
  readonly cancelText: string
  readonly isDestructive: boolean
  readonly isSubmitting: boolean
}

interface CancelButtonProps {
  readonly onClick: () => void
  readonly text: string
  readonly disabled: boolean
}

interface ConfirmButtonProps {
  readonly onClick: () => void
  readonly text: string
  readonly isDestructive: boolean
  readonly isSubmitting: boolean
}

export const DeleteAllConfirmationDialog: FC<DeleteAllConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
}) => {
  const theme = useTheme()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = useCallback(async (): Promise<void> => {
    setIsSubmitting(true)
    try {
      await onConfirm()
      onClose()
    } catch {
      // Stay open on failure — user can retry or cancel
    } finally {
      setIsSubmitting(false)
    }
  }, [onConfirm, onClose])

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' && !isSubmitting) {
      event.preventDefault()
      void handleConfirm()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      onKeyDown={handleKeyDown}
      maxWidth="sm"
      fullWidth={false}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      sx={dialogSx(theme)}
    >
      <DialogHeader title={title} />
      <DialogBody>{children}</DialogBody>
      <DialogFooter
        onClose={onClose}
        onConfirm={handleConfirm}
        confirmText={confirmText}
        cancelText={cancelText}
        isDestructive={isDestructive}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  )
}

const DialogHeader: FC<DialogHeaderProps> = ({ title }) => {
  const theme = useTheme()

  return (
    <DialogTitle id="confirmation-dialog-title" sx={dialogTitleSx(theme)}>
      {title}
    </DialogTitle>
  )
}

const DialogBody: FC<DialogBodyProps> = ({ children }) => (
  <DialogContent sx={dialogContentSx}>{children}</DialogContent>
)

const DialogFooter: FC<DialogFooterProps> = ({
  onClose,
  onConfirm,
  confirmText,
  cancelText,
  isDestructive,
  isSubmitting,
}) => (
  <DialogActions sx={dialogActionsSx}>
    <CancelButton onClick={onClose} text={cancelText} disabled={isSubmitting} />
    <ConfirmButton
      onClick={onConfirm}
      text={confirmText}
      isDestructive={isDestructive}
      isSubmitting={isSubmitting}
    />
  </DialogActions>
)

const CancelButton: FC<CancelButtonProps> = ({ onClick, text, disabled }) => {
  const theme = useTheme()

  return (
    <Button
      onClick={onClick}
      variant="outlined"
      size="medium"
      disabled={disabled}
      sx={cancelButtonSx(theme)}
    >
      {text}
    </Button>
  )
}

const ConfirmButton: FC<ConfirmButtonProps> = ({ onClick, text, isDestructive, isSubmitting }) => {
  const theme = useTheme()

  return (
    <Button
      onClick={onClick}
      variant="contained"
      size="medium"
      disabled={isSubmitting}
      sx={confirmButtonSx(theme, isDestructive)}
    >
      {isSubmitting ? <CircularProgress size={20} color="inherit" /> : text}
    </Button>
  )
}

const dialogSx = (theme: Theme): SxProps<Theme> => ({
  '& .MuiDialog-paper': {
    borderRadius: 2,
    boxShadow: theme.shadows[8],
    backgroundColor: 'background.paper',
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 400,
    maxWidth: 480,
    backgroundImage: 'none',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(
      theme.palette.mode === 'dark' ? theme.palette.common.black : '#3E2723',
      0.5,
    ),
    backdropFilter: 'blur(2px)',
  },
})

const dialogTitleSx = (theme: Theme): SxProps<Theme> => ({
  pb: 2,
  pt: 3,
  fontWeight: 500,
  fontSize: '1.125rem',
  color: 'text.primary',
  fontFamily: theme.typography.fontFamily,
})

const dialogContentSx: SxProps<Theme> = {
  pb: 3,
  pt: 1,
}

const dialogActionsSx: SxProps<Theme> = {
  px: 3,
  pb: 3,
  pt: 2,
  gap: 1.5,
  justifyContent: 'flex-end',
}

const cancelButtonSx = (theme: Theme): SxProps<Theme> => ({
  color: 'text.secondary',
  borderColor: 'divider',
  backgroundColor: 'transparent',
  fontFamily: theme.typography.fontFamily,
  '&:hover': {
    backgroundColor: 'action.hover',
    borderColor: 'text.secondary',
    color: 'text.primary',
  },
})

const confirmButtonSx = (theme: Theme, isDestructive: boolean): SxProps<Theme> => ({
  backgroundColor: isDestructive ? theme.palette.error.main : theme.palette.primary.main,
  color: isDestructive ? theme.palette.error.contrastText : theme.palette.primary.contrastText,
  fontFamily: theme.typography.fontFamily,
  '&:hover': {
    backgroundColor: isDestructive ? theme.palette.error.dark : theme.palette.primary.dark,
  },
})
